import { useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, CircularProgress, makeStyles, MenuItem } from '@material-ui/core';
import _ from 'lodash';
import { i18n } from '@lingui/core';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { DISCOVER_FIELD, EXCLUDE_FIELD, SUBNET_FIELD, VALUE_FIELD } from './CollectorIpFormConstants';
import { useDataService } from '../../../services/ApiDataService';
import useAlerts from '../../../stores/AlertsStore';
import { buildLinkUrl } from '../../links/Link';
import NgpSelect from '../../form/NgpSelect';
import { COLLECTORS_DESTCOLLECTOR_FIELD } from './CollectorsAdminConstants';
import { RowFlexLayout } from '../../layout';
import useUiToasts, { createToastConfig, INFO } from '../../../stores/ToastStore';

const formId = 'collectFrmIpScope';
const addButtonText = t`Transfer IP Scope`;
const defaultFormValues = {};

export const useStyles = makeStyles({
    root: {
        padding: 0,
        margin: 0,
        display: 'flex',
    },
    collectorSelection: {
        '& .MuiSelect-select': {
            minWidth: 200,
        },
    },
});

export default function CollectorMoveIpScopeForm({
    associatedCollector,
    transferrableCollectors,
    setSubnetRangeCache,
    selectedRecord = {},
    setSelectedRecord,
    open,
    setOpen,
    onMoveSuccess,
}) {
    const classes = useStyles();
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: {
            [EXCLUDE_FIELD]: false,
            [DISCOVER_FIELD]: true,
        },
    });
    const internalId = useRef(null);
    const buttonText = useRef(addButtonText);
    const dataService = useDataService();
    const [, alertsActions] = useAlerts();
    const [, uiToastActions] = useUiToasts();
    const { handleSubmit, formState, reset } = methods;
    const [saving, setSaving] = useState(false);
    const [subnet, setSubnet] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [childIpScopes, setChildIpScopes] = useState([]);

    useEffect(() => {
        const showScopesUrl = '/ngp/v2/inventory/collectors/:_id/show-scopes';
        const collectorId = associatedCollector._id;

        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];

            const sub = selectedRecord[VALUE_FIELD][SUBNET_FIELD];
            setSubnet(sub);
            const query = { subnet: sub };
            if (collectorId) {
                const url = buildLinkUrl({ target: showScopesUrl }, { _id: collectorId }, null);
                dataService
                    .get(url, query)
                    .then((response) => _.reject(response.ipScopes, { value: { subnet: sub } }))
                    .then((childScopes) => {
                        setLoaded(true);
                        setChildIpScopes(childScopes);
                    })
                    .catch((e) => {
                        if (e?.responseBody?.error) {
                            alertsActions.createErrorAlert(e.responseBody.error);
                        }
                    });
            }
        }
    }, [selectedRecord, alertsActions, associatedCollector, dataService]);

    const handleCleanup = () => {
        internalId.current = null;
        buttonText.current = addButtonText;
        setSelectedRecord(undefined);
        reset(defaultFormValues);
    };

    const transferScope = (destCollector) => {
        if (!associatedCollector) {
            throw new Error('transferScope()- Missing source collector');
        }

        if (!destCollector) {
            throw new Error('transferScope()- Missing destination collector');
        }

        if (!subnet) {
            throw new Error('transferScope()- Missing subnet');
        }

        const body = {
            dest: destCollector._id,
            subnet,
        };

        const moveScopesUrl = '/ngp/v2/inventory/collectors/:_id/move-scopes';
        const collectorId = associatedCollector._id;

        if (!_.isEmpty(selectedRecord)) {
            const sub = selectedRecord[VALUE_FIELD][SUBNET_FIELD];
            setSubnet(sub);
            if (collectorId) {
                const url = buildLinkUrl({ target: moveScopesUrl }, { _id: collectorId }, null);
                dataService
                    .post(url, body)
                    .then(() => {
                        setOpen(false);
                        // Remove the subnet and its children from the subnet range cache.
                        const childSubnets = _.map(childIpScopes, 'value.subnet');
                        setSubnetRangeCache((prev) => _.omit(prev, [subnet, ...childSubnets]));
                        if (_.isFunction(onMoveSuccess)) {
                            onMoveSuccess({ movedSubnet: subnet, childSubnets });
                        }
                        uiToastActions.createToast(
                            createToastConfig(INFO, i18n._(t`Successfully transferred IP scope(s)`))
                        );
                    })
                    .catch((e) => {
                        if (e?.responseBody?.error) {
                            alertsActions.createErrorAlert(e.responseBody.error);
                        }
                    });
            }
        }
    };

    const handleFormSubmit = async (dataToSave = {}) => {
        if (internalId) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        setSaving(true);

        try {
            await transferScope(dataToSave[COLLECTORS_DESTCOLLECTOR_FIELD]);
            handleCleanup();
        } finally {
            setSaving(false);
        }
    };

    const startIcon = saving && <CircularProgress color="inherit" variant="indeterminate" size={16} />;

    return (
        <NgpTwoButtonDialog
            title={<Trans>Transfer IP Scope: {subnet}</Trans>}
            confirmButtonText={buttonText.current}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid || saving}
            confirmButtonProps={{ startIcon }}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={formId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <FormSection>
                        <div>
                            <Trans>
                                Transfers an IP scope from one collector to another. This will take effect immediately
                                after clicking transfer.
                            </Trans>
                        </div>
                        {loaded && childIpScopes && childIpScopes.length > 0 && (
                            <>
                                <Box mt={2}>
                                    <Trans>
                                        The following child IP scopes will automatically be transferred as well:
                                    </Trans>
                                </Box>
                                <Box component="ul" pl={4} mt={1} mb={0}>
                                    {childIpScopes.map((ipScope) => (
                                        <li key={ipScope.value.subnet}>
                                            <Trans>{ipScope.value.subnet}</Trans>
                                        </li>
                                    ))}
                                </Box>
                            </>
                        )}
                        <RowFlexLayout vCenter>
                            <Box mt={2}>
                                <Trans>Please select a destination collector:</Trans>
                            </Box>
                            <Box flexGrow={1} />
                            <Box ml={2} mt={-2}>
                                <NgpSelect
                                    className={classes.collectorSelection}
                                    fieldName={COLLECTORS_DESTCOLLECTOR_FIELD}
                                    label={<Trans>Destination Collector</Trans>}
                                    required
                                >
                                    {_.map(transferrableCollectors, (value) => (
                                        <MenuItem key={value._id} value={value}>
                                            {value.name}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>
                            </Box>
                        </RowFlexLayout>
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
